(function attachTailiVideoLoader(global) {
  'use strict';

  function waitForPlayableData(video, source) {
    if (video.readyState >= 2) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
      };
      const handleLoadedData = () => {
        cleanup();
        resolve();
      };
      const handleError = () => {
        cleanup();
        reject(new Error(`Could not decode ${source}`));
      };

      video.addEventListener('loadeddata', handleLoadedData, { once: true });
      video.addEventListener('error', handleError, { once: true });
    });
  }

  function normalizeSource(source) {
    try {
      const base = global.document?.baseURI || global.location?.href || 'https://tailistore.com/';
      return new URL(source, base).href;
    } catch {
      return source;
    }
  }

  function hasMatchingSource(video, source) {
    const activeSource = video.currentSrc || video.src || '';
    return Boolean(activeSource) && normalizeSource(activeSource) === normalizeSource(source);
  }

  function loadDirect(video, source) {
    if (!source) return Promise.resolve();

    video.preload = 'auto';
    if (!hasMatchingSource(video, source)) {
      video.src = source;
      video.load();
    }
    return waitForPlayableData(video, source);
  }

  function bufferedEnd(video) {
    const ranges = video.buffered;
    if (!ranges || !ranges.length) return 0;
    return ranges.end(ranges.length - 1);
  }

  function waitForBufferedSeconds(video, seconds) {
    if (!seconds || bufferedEnd(video) >= seconds) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        video.removeEventListener('progress', handleProgress);
        video.removeEventListener('canplaythrough', handleCanPlayThrough);
        video.removeEventListener('error', handleError);
      };
      const finish = () => {
        cleanup();
        resolve();
      };
      const handleProgress = () => {
        if (bufferedEnd(video) >= seconds) finish();
      };
      const handleCanPlayThrough = () => finish();
      const handleError = () => {
        cleanup();
        reject(new Error('Primary journey video buffering failed'));
      };

      video.addEventListener('progress', handleProgress);
      video.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
      video.addEventListener('error', handleError, { once: true });
    });
  }

  function defaultScheduleDeferred(task) {
    if (typeof global.requestIdleCallback === 'function') {
      global.requestIdleCallback(task, { timeout: 2500 });
      return;
    }

    global.setTimeout(task, 0);
  }

  async function loadPrimaryThenDeferSecondary(options) {
    const {
      primary,
      primarySource,
      secondary,
      secondarySource,
      primaryBufferSeconds = 12,
      scheduleDeferred = defaultScheduleDeferred,
      onSecondaryError = () => {}
    } = options;

    await loadDirect(primary, primarySource);
    scheduleDeferred(async () => {
      try {
        await waitForBufferedSeconds(primary, primaryBufferSeconds);
        return await loadDirect(secondary, secondarySource);
      } catch (error) {
        onSecondaryError(error);
        return undefined;
      }
    });
  }

  const api = {
    loadDirect,
    waitForBufferedSeconds,
    loadPrimaryThenDeferSecondary
  };

  global.TailiVideoLoader = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
